import React, { useState } from "react";
import { SITE_CONTENT } from "../content";

// ── Shared input styles ─────────────────────────────────────────
const inputBase =
  "w-full bg-[#0d1f0d]/80 border border-white/40 text-white placeholder-white/60 text-sm py-3.5 px-5 rounded-xl outline-none focus:border-[#E8A040] focus:ring-1 focus:ring-[#E8A040]/30 transition-all duration-300";
const selectBase =
  "w-full bg-[#0d1f0d]/80 border border-white/40 text-white text-sm py-3.5 px-5 rounded-xl outline-none focus:border-[#E8A040] transition-all duration-300 [color-scheme:dark] appearance-none cursor-pointer";

// ── Selectable Menu Item Card ───────────────────────────────────
const MenuItemCard = ({ item, selected, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(item.name)}
    className={`relative w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
      selected
        ? "border-[#E8A040] bg-[#E8A040]/20 shadow-[0_0_16px_rgba(232,160,64,0.2)]"
        : "border-white/20 bg-white/10 hover:border-white/50 hover:bg-white/15"
    }`}
  >
    {selected && (
      <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#E8A040] flex items-center justify-center">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    )}
    <p className={`text-sm font-semibold pr-6 leading-snug ${selected ? "text-[#FFD580]" : "text-white"}`}>
      {item.name}
    </p>
    {item.desc && (
      <p className={`text-xs mt-1 leading-relaxed ${selected ? "text-white/70" : "text-white/55"}`}>
        {item.desc}
      </p>
    )}
    {item.price === "Premium" && (
      <span className="inline-block mt-1.5 text-[9px] tracking-widest uppercase text-amber-400 font-semibold bg-amber-400/15 px-2 py-0.5 rounded-full border border-amber-400/30">
        Premium
      </span>
    )}
  </button>
);

const WEB3FORMS_KEY = "b04ebf4c-3064-4a9a-acaf-30db5449f1a3";

const ReservationSection = () => {
  const { backgroundImage, subtitle, title, description } =
    SITE_CONTENT.pages.homePage.reservationSection;

  // All 9 real menu sections from menuPage
  const menuSections = SITE_CONTENT.pages.menuPage.sections;

  const [step, setStep] = useState(1);
  const [menuTab, setMenuTab] = useState(menuSections[0].id);

  const [form1, setForm1] = useState({ name: "", email: "", phone: "" });
  const [phoneError, setPhoneError] = useState("");

  const [form2, setForm2] = useState({
    eventType: "",
    eventDate: "",
    eventLocation: "",
    foodType: "non-veg",
    guestCount: "",
  });

  // Selection state: { [sectionId]: string[] } — stores item names
  const [selected, setSelected] = useState(
    () => Object.fromEntries(menuSections.map((s) => [s.id, []]))
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleForm1Change = (e) =>
    setForm1((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleForm2Change = (e) =>
    setForm2((p) => ({ ...p, [e.target.name]: e.target.value }));
  const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone.replace(/\s/g, ""));

  const toggleItem = (sectionId, itemName) => {
    setSelected((p) => ({
      ...p,
      [sectionId]: p[sectionId].includes(itemName)
        ? p[sectionId].filter((x) => x !== itemName)
        : [...p[sectionId], itemName],
    }));
  };

  const totalSelected = Object.values(selected).reduce((acc, arr) => acc + arr.length, 0);

  const buildMenuSummary = () => {
    const parts = menuSections
      .filter((s) => selected[s.id]?.length > 0)
      .map((s) => `${s.label}: ${selected[s.id].join(", ")}`);
    return parts.join(" | ") || "No specific items selected";
  };

  const handleStep1Next = (e) => {
    e.preventDefault();
    setPhoneError("");
    if (!isValidPhone(form1.phone)) {
      setPhoneError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    setStep(2);
  };

  const handleStep2Next = (e) => {
    e.preventDefault();
    setStep(3);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const menuPayload = {};
      menuSections.forEach((s) => {
        if (selected[s.id]?.length > 0) {
          menuPayload[s.label] = selected[s.id].join(", ");
        }
      });

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Event Enquiry — Nawabi's Caterers (${form2.eventType || "General"})`,
          from_name: form1.name,
          Name: form1.name,
          Email: form1.email,
          "Phone Number": `+91 ${form1.phone}`,
          "Event Type": form2.eventType,
          "Event Date": form2.eventDate,
          "Event Location": form2.eventLocation,
          "Food Preference": form2.foodType,
          "Guest Count": form2.guestCount,
          "Custom Menu": buildMenuSummary(),
          ...menuPayload,
        }),
      });
      const result = await res.json();
      if (result.success) setSubmitted(true);
      else alert("Submission failed. Please call us directly.");
    } catch {
      alert("A network error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setStep(1);
    setForm1({ name: "", email: "", phone: "" });
    setForm2({ eventType: "", eventDate: "", eventLocation: "", foodType: "non-veg", guestCount: "" });
    setSelected(Object.fromEntries(menuSections.map((s) => [s.id, []])));
    setMenuTab(menuSections[0].id);
  };

  const steps = ["Your Info", "Event Details", "Your Menu"];
  const activeSection = menuSections.find((s) => s.id === menuTab) || menuSections[0];

  return (
    <section id="booking-section" className="relative w-full flex items-center justify-center overflow-hidden py-24 scroll-mt-20">
      {/* Background */}
      <img
        src={backgroundImage}
        alt="Nawabi catering event"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-[#0a160a]/85" />

      {/* Decorative gold line top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8A040] to-transparent opacity-60" />

      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 sm:px-6">
        {/* ── Section Header ── */}
        <div className="text-center mb-8">
          <span className="inline-block text-[#E8A040] text-[10px] tracking-[0.4em] uppercase font-semibold mb-3 border border-[#E8A040]/40 rounded-full px-4 py-1">
            {subtitle}
          </span>
          <h2 className="text-white text-4xl sm:text-5xl font-light mb-3">{title}</h2>
          <p className="text-white/65 text-sm leading-relaxed max-w-md mx-auto">{description}</p>
        </div>

        {/* ── MAIN CARD ── */}
        <div className="bg-[#0d1f0d]/90 backdrop-blur-md border border-white/15 rounded-3xl shadow-2xl overflow-hidden">

          {/* Success Screen */}
          {submitted ? (
            <div className="p-10 text-center animate-fadeIn">
              <div className="w-20 h-20 rounded-full bg-[#22c55e]/20 border-2 border-[#22c55e]/50 flex items-center justify-center mx-auto mb-6">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="text-white text-3xl font-light mb-3">Enquiry Submitted!</h3>
              <p className="text-white/65 text-sm leading-relaxed max-w-sm mx-auto mb-6">
                Thank you, <span className="text-white font-semibold">{form1.name}</span>. Our team will call you on{" "}
                <span className="text-[#E8A040] font-semibold">+91 {form1.phone}</span> within 24 hours.
              </p>
              {totalSelected > 0 && (
                <div className="bg-[#E8A040]/10 border border-[#E8A040]/30 rounded-2xl p-4 mb-6 text-left">
                  <p className="text-[#E8A040] text-xs font-bold uppercase tracking-wider mb-2">Your Selected Menu</p>
                  <p className="text-white/70 text-xs leading-relaxed">{buildMenuSummary()}</p>
                </div>
              )}
              <button
                onClick={resetForm}
                className="px-8 py-3 border-2 border-white/30 text-white text-xs tracking-[0.2em] uppercase rounded-full hover:border-[#E8A040] hover:text-[#E8A040] transition-all duration-300"
              >
                Submit Another Enquiry
              </button>
            </div>
          ) : (
            <>
              {/* Step Progress Bar */}
              <div className="bg-white/5 border-b border-white/10 px-8 py-5">
                <div className="flex items-center gap-3">
                  {steps.map((label, i) => {
                    const s = i + 1;
                    const done = step > s;
                    const isActive = step === s;
                    return (
                      <React.Fragment key={s}>
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-500 flex-shrink-0 ${
                              done
                                ? "bg-[#E8A040] border-[#E8A040] text-white"
                                : isActive
                                ? "border-white bg-white/10 text-white"
                                : "border-white/25 text-white/30"
                            }`}
                          >
                            {done ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              s
                            )}
                          </div>
                          <span
                            className={`text-[11px] font-semibold tracking-wider uppercase whitespace-nowrap hidden sm:block ${
                              isActive ? "text-white" : done ? "text-[#E8A040]" : "text-white/30"
                            }`}
                          >
                            {label}
                          </span>
                        </div>
                        {s < 3 && (
                          <div
                            className={`flex-1 h-0.5 rounded-full transition-all duration-700 ${
                              step > s ? "bg-[#E8A040]" : "bg-white/15"
                            }`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Form Body */}
              <div className="p-8 sm:p-10">

                {/* ══ STEP 1 — Contact Info ══ */}
                {step === 1 && (
                  <form onSubmit={handleStep1Next} className="space-y-5 animate-fadeIn">
                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Full Name</label>
                      <input type="text" name="name" value={form1.name} onChange={handleForm1Change} placeholder="e.g. Mohammed Rizwan" required className={inputBase} />
                    </div>
                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Email Address</label>
                      <input type="email" name="email" value={form1.email} onChange={handleForm1Change} placeholder="your@email.com" required className={inputBase} />
                    </div>
                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Mobile Number</label>
                      <div className="flex gap-2">
                        <span className="flex items-center px-4 bg-white/10 border border-white/40 rounded-xl text-white font-semibold text-sm flex-shrink-0">+91</span>
                        <input
                          type="tel"
                          name="phone"
                          value={form1.phone}
                          onChange={(e) => { handleForm1Change(e); setPhoneError(""); }}
                          placeholder="9876543210"
                          maxLength={10}
                          required
                          className={inputBase}
                        />
                      </div>
                      {phoneError && <p className="text-red-400 text-xs mt-2 flex items-center gap-1">⚠ {phoneError}</p>}
                    </div>
                    <div className="pt-2">
                      <button type="submit" className="w-full py-4 bg-[#E8A040] hover:bg-[#d4922e] text-white text-sm tracking-[0.15em] uppercase font-bold rounded-xl transition-all duration-300 shadow-lg shadow-[#E8A040]/20 hover:scale-[1.02]">
                        Continue to Event Details →
                      </button>
                    </div>
                  </form>
                )}

                {/* ══ STEP 2 — Event Details ══ */}
                {step === 2 && (
                  <form onSubmit={handleStep2Next} className="space-y-5 animate-fadeIn">
                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Event Type</label>
                      <div className="relative">
                        <select name="eventType" value={form2.eventType} onChange={handleForm2Change} required className={selectBase}>
                          <option value="" disabled className="bg-[#0d1f0d]">Select Event Type</option>
                          {["Wedding & Reception","Birthday Party","Corporate Event","Religious Ceremony","Festival Catering","College / School Event","Mass Event / Mela","Family Gathering","Other"].map((t) => (
                            <option key={t} value={t} className="bg-[#0d1f0d]">{t}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Event Date</label>
                      <input type="date" name="eventDate" value={form2.eventDate} onChange={handleForm2Change} required min={new Date().toISOString().split("T")[0]} className={`${inputBase} [color-scheme:dark]`} />
                    </div>

                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Event Location</label>
                      <input type="text" name="eventLocation" value={form2.eventLocation} onChange={handleForm2Change} placeholder="City / Venue name" required className={inputBase} />
                    </div>

                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Food Preference</label>
                      <div className="flex gap-3">
                        {[
                          { v: "non-veg", l: "🍗 Non-Veg" },
                          { v: "veg",     l: "🥗 Veg" },
                          { v: "both",    l: "🍽️ Both" },
                        ].map(({ v, l }) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setForm2((p) => ({ ...p, foodType: v }))}
                            className={`flex-1 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                              form2.foodType === v
                                ? "border-[#E8A040] bg-[#E8A040]/20 text-[#FFD580]"
                                : "border-white/25 text-white/60 hover:border-white/50 hover:text-white"
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-xs font-semibold uppercase tracking-widest mb-2">Estimated Guest Count</label>
                      <input type="number" name="guestCount" value={form2.guestCount} onChange={handleForm2Change} placeholder="e.g. 300" min="50" required className={inputBase} />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => setStep(1)} className="px-6 py-4 border-2 border-white/30 text-white text-sm font-semibold rounded-xl hover:border-white/60 transition-all duration-300">
                        ← Back
                      </button>
                      <button type="submit" className="flex-1 py-4 bg-[#E8A040] hover:bg-[#d4922e] text-white text-sm tracking-[0.15em] uppercase font-bold rounded-xl transition-all duration-300 shadow-lg hover:scale-[1.02]">
                        Build My Menu →
                      </button>
                    </div>
                  </form>
                )}

                {/* ══ STEP 3 — Menu Builder (all 9 sections) ══ */}
                {step === 3 && (
                  <form onSubmit={handleFinalSubmit} className="animate-fadeIn">

                    {/* Category Tabs — scrollable row */}
                    <div className="flex gap-2 mb-5 overflow-x-auto pb-2 scrollbar-none">
                      {menuSections.map((sec) => {
                        const count = selected[sec.id]?.length || 0;
                        const isActive = menuTab === sec.id;
                        return (
                          <button
                            key={sec.id}
                            type="button"
                            onClick={() => setMenuTab(sec.id)}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 border-2 ${
                              isActive
                                ? "bg-[#E8A040] border-[#E8A040] text-white shadow-lg shadow-[#E8A040]/25"
                                : "border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:bg-white/10"
                            }`}
                          >
                            <span className="text-sm">{sec.icon}</span>
                            <span className="whitespace-nowrap">{sec.label}</span>
                            {count > 0 && (
                              <span
                                className={`rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold ${
                                  isActive ? "bg-white text-[#E8A040]" : "bg-[#E8A040] text-white"
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Active section subtitle */}
                    <p className="text-white/60 text-xs font-medium mb-4">
                      {activeSection.icon} <span className="font-semibold text-white/80">{activeSection.label}</span> — tap items to add to your menu
                    </p>

                    {/* Items Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1 scrollbar-thin">
                      {activeSection.items.map((item) => (
                        <MenuItemCard
                          key={item.name}
                          item={item}
                          selected={selected[activeSection.id]?.includes(item.name) || false}
                          onToggle={(name) => toggleItem(activeSection.id, name)}
                        />
                      ))}
                    </div>

                    {/* Selected Summary chips */}
                    {totalSelected > 0 && (
                      <div className="mt-5 p-4 rounded-2xl border border-[#E8A040]/40 bg-[#E8A040]/10">
                        <p className="text-[#FFD580] text-xs font-bold mb-3 uppercase tracking-wider">
                          Your Menu — {totalSelected} item{totalSelected !== 1 ? "s" : ""} selected
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {menuSections.flatMap((sec) =>
                            (selected[sec.id] || []).map((name) => ({
                              secId: sec.id,
                              name,
                            }))
                          ).map(({ secId, name }) => (
                            <span
                              key={`${secId}-${name}`}
                              className="flex items-center gap-1.5 bg-[#E8A040]/20 border border-[#E8A040]/40 text-[#FFD580] text-xs rounded-full px-3 py-1.5 font-medium"
                            >
                              {name}
                              <button
                                type="button"
                                onClick={() => toggleItem(secId, name)}
                                className="text-[#E8A040] hover:text-white transition-colors leading-none"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="px-6 py-4 border-2 border-white/30 text-white text-sm font-semibold rounded-xl hover:border-white/60 transition-all duration-300"
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`flex-1 py-4 bg-[#E8A040] text-white text-sm tracking-[0.15em] uppercase font-bold rounded-xl transition-all duration-300 shadow-lg ${
                          isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:bg-[#d4922e] hover:scale-[1.02]"
                        }`}
                      >
                        {isSubmitting
                          ? "Submitting…"
                          : `Submit Enquiry${totalSelected > 0 ? ` (${totalSelected} dishes)` : ""} →`}
                      </button>
                    </div>
                    <p className="text-white/35 text-[11px] text-center mt-4">
                      Menu selection is optional — our team will guide you.
                    </p>
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Decorative gold line bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8A040] to-transparent opacity-60" />
    </section>
  );
};

export default ReservationSection;
